import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  updateProfile
} from 'firebase/auth';
import { doc, setDoc, Timestamp } from 'firebase/firestore';
import { v4 as uuidv4 } from 'uuid';
import customEmailService from '../services/customEmailService';
import { auth, db } from '../firebase/config';
import userService from '../services/userService';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  async function signup(email, password, displayName, userType = 'influencer', additionalData = {}) {
    try {
      // Check if we should use development mode
      const isDevelopment = process.env.NODE_ENV === 'development' && process.env.REACT_APP_USE_MOCK_FIREBASE === 'true';
      
      if (isDevelopment) {

        // Wait for a moment to simulate network request
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        // Create a mock user for development
        const mockUser = {
          uid: `dev-${userType}-${Date.now()}`,
          email,
          displayName,
          userType,
          emailVerified: false,
          ...additionalData,
          createdAt: new Date().toISOString(),
          profileCompleted: false
        };
        
        // Store in localStorage
        localStorage.setItem('devModeUser', JSON.stringify(mockUser));
        
        return mockUser;
      } else {
        // Real Firebase authentication for production
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        const user = userCredential.user;
        
        // Update profile with display name
        await updateProfile(user, { displayName });
        
        // Send email verification using custom email service (bypasses Firebase templates)

        
        // Use custom email service to completely bypass Firebase's email system
        const emailResult = await customEmailService.sendEmailVerification(user);
        
        if (!emailResult.success) {
          console.error('Custom email verification failed:', emailResult.error);
          throw new Error(emailResult.error);
        }
        

        
        // Create user document in Firestore using userService
        const userData = {
          uid: user.uid,
          email,
          displayName,
          userType,
          emailVerified: false,
          ...additionalData,
          profileCompleted: false
        };
        
        // Use the createUser method which handles both user types
        await userService.createUser(user.uid, userData);
        
        return user;
      }
    } catch (error) {
      console.error('Error during signup:', error);
      throw error;
    }
  }

  async function login(email, password, userType = 'influencer') {
    // Check if we should use development mode
    const isDevelopment = process.env.REACT_APP_USE_MOCK_FIREBASE === 'true';
    
    if (isDevelopment) {

      // Wait for a moment to simulate network request
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Create a mock user for development
      const mockUser = {
        uid: `dev-${userType}-${Date.now()}`,
        email,
        displayName: email.split('@')[0],
        userType,
        companyName: userType === 'brand' ? email.split('@')[0] : null,
        createdAt: new Date().toISOString(),
        profileCompleted: true
      };
      
      // Store in localStorage
      localStorage.setItem('devModeUser', JSON.stringify(mockUser));
      
      // Update state
      setCurrentUser(mockUser);
      setUserProfile(mockUser);
      
      return mockUser;
    } else {
      // Real Firebase authentication for production
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      // Fetch user profile to verify account type
      const userProfile = await userService.getUserById(user.uid);
      
      // Verify that the selected account type matches the user's actual type
      if (userProfile && userProfile.userType !== userType) {
        // Create a custom error object without logging to console
        const error = new Error(`This account is registered as a ${userProfile.userType}. Please select the correct account type.`);
        
        // In production, we don't want to log this to console
        if (process.env.NODE_ENV === 'production') {
          // Prevent error from being logged to console
          error.suppressConsole = true;
        }
        
        throw error;
      }
      
      // Update login timestamp
      await userService.updateLoginTimestamp(user.uid);
      
      return user;
    }
  }

  function logout() {
    // Check for development mode
    const devModeUser = localStorage.getItem('devModeUser');
    if (devModeUser) {
      // Clear development mode user
      localStorage.removeItem('devModeUser');
      setCurrentUser(null);
      setUserProfile(null);
      return Promise.resolve();
    }
    
    // Regular Firebase logout for production
    return signOut(auth);
  }

  async function resetPassword(email) {
    try {
      // Check if we should use development mode
      const isDevelopment = process.env.REACT_APP_USE_MOCK_FIREBASE === 'true';
      
      if (isDevelopment) {
        // Simulate password reset in development mode
        await new Promise(resolve => setTimeout(resolve, 1000));
        return;
      } else {
        // Use custom password reset with Cloud Function
        // This creates a token in emailTokens collection which triggers sendCustomPasswordReset
        const token = uuidv4();
        const expiresAt = new Date(Date.now() + 24 * 60 * 60 * 1000); // 24 hours
        
        const tokenData = {
          token,
          userId: null, // We don't have userId yet, will be looked up by email
          email,
          type: 'password-reset',
          createdAt: Timestamp.now(),
          expiresAt: Timestamp.fromDate(expiresAt),
          used: false
        };

        // Store token in Firestore - this will trigger the sendCustomPasswordReset Cloud Function
        await setDoc(doc(db, 'emailTokens', token), tokenData);
        

      }
    } catch (error) {
      console.error('Error sending password reset email:', error);
      throw error;
    }
  }

  async function fetchUserProfile(user) {
    if (!user) return null;
    
    try {
      // Use userService to get user profile
      const userProfile = await userService.getUserById(user.uid);
      if (userProfile) {
        setUserProfile(userProfile);
        return userProfile;
      }
      return null;
    } catch (error) {
      console.error("Error fetching user profile:", error);
      return null;
    }
  }

  useEffect(() => {
    // Check if we should use development mode
    const isDevelopment = process.env.REACT_APP_USE_MOCK_FIREBASE === 'true';
    
    // Check for development mode user in localStorage
    const devModeUser = localStorage.getItem('devModeUser');
    
    if (isDevelopment && devModeUser) {
      const parsedUser = JSON.parse(devModeUser);

      setCurrentUser(parsedUser);
      setUserProfile({
        uid: parsedUser.uid,
        email: parsedUser.email,
        displayName: parsedUser.displayName,
        userType: parsedUser.userType || 'influencer',
        createdAt: new Date().toISOString(),
        profileCompleted: true
      });
      setLoading(false);
      return () => {};
    }
    
    // Regular Firebase auth for production
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      if (user) {
        await fetchUserProfile(user);
      } else {
        setUserProfile(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    userProfile,
    signup,
    login,
    logout,
    resetPassword,
    fetchUserProfile
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}
